import { useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';

import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import { PANEL_WIDTH } from '@cartwhl/ui/layout/Panel';

import CharacterPanel from 'components/CharacterPanel';
import { GEN_SUBPANEL_KEY } from 'components/CharacterPanel/constants';
import {
  HistoryPanel,
  MotionProjectHistoryPanel,
  PoseHistoryPanel,
} from 'components/HistoryPanel';
import { SceneSettingsPanel } from 'components/SceneSettingsPanel';
import { projectStore, uiStore } from 'stores';

import BottomSheet, { BottomSheetRef } from './BottomSheet';
import GetStartedPanel from './GetStartedPanel';

const shortPanels = ['character', 'label', 'advanced'];

export default function Panels() {
  const sheetRef = useRef<BottomSheetRef>(null);
  const { openPanel, subPanelKey } = useSnapshot(uiStore.state);
  const { currentProject } = useSnapshot(projectStore.state);
  const isPoseMode = currentProject.data?.type === 'POSE';
  const isMotionProject = currentProject.data?.type === 'MOTION';

  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const closePanel = () => uiStore.setOpenPanel('');

  useEffect(() => {
    if (isDesktop) return sheetRef.current?.close();
    if (openPanel) {
      sheetRef.current?.open();
    } else {
      sheetRef.current?.close();
    }
  }, [isDesktop, openPanel]);

  return (
    <>
      {isDesktop ? (
        <div
          className="h-full transition-[width] duration-200"
          style={{ width: openPanel ? PANEL_WIDTH : 0 }}
        >
          <GetStartedPanel
            isOpen={openPanel === 'start'}
            close={closePanel}
            exitTransitionDuration={200}
          />
          <CharacterPanel
            isOpen={openPanel === 'character'}
            close={closePanel}
            exitTransitionDuration={200}
          />
          <SceneSettingsPanel
            isOpen={openPanel === 'advanced'}
            close={closePanel}
            exitTransitionDuration={200}
          />
          <HistoryPanel
            isOpen={openPanel === 'history' && !isPoseMode && !isMotionProject}
            close={closePanel}
            exitTransitionDuration={200}
          />
          <PoseHistoryPanel
            isOpen={openPanel === 'history' && isPoseMode}
            close={closePanel}
            exitTransitionDuration={200}
          />
          <MotionProjectHistoryPanel
            isOpen={openPanel === 'history' && isMotionProject}
            close={closePanel}
            exitTransitionDuration={200}
          />
        </div>
      ) : (
        <BottomSheet
          expansionSwitchThreshold={2000}
          ref={sheetRef}
          className="z-30 overflow-x-hidden"
          onClose={closePanel}
          detents={
            subPanelKey === GEN_SUBPANEL_KEY
              ? ['90%']
              : shortPanels.includes(openPanel)
                ? ['50%']
                : ['90%']
          }
        >
          <GetStartedPanel isOpen={openPanel === 'start'} close={closePanel} />
          <CharacterPanel
            isOpen={openPanel === 'character'}
            close={closePanel}
          />
          <SceneSettingsPanel
            isOpen={openPanel === 'advanced'}
            close={closePanel}
          />
          <HistoryPanel
            isOpen={openPanel === 'history' && !isPoseMode && !isMotionProject}
            close={closePanel}
            exitTransitionDuration={200}
          />
          <PoseHistoryPanel
            isOpen={openPanel === 'history' && isPoseMode}
            close={closePanel}
            exitTransitionDuration={200}
          />
          <MotionProjectHistoryPanel
            isOpen={openPanel === 'history' && isMotionProject}
            close={closePanel}
            exitTransitionDuration={200}
          />
        </BottomSheet>
      )}
    </>
  );
}
